import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { IconAlertTriangle, IconBell, IconBuilding, IconMessageCircle, IconPackage, IconPlayerSkipForward, IconRoute, IconUserCog, IconUserPlus, IconChartBar, IconChecklist, IconDots, IconCheck, IconClockHour4, IconFileText, IconForbid2, IconLayoutGrid, IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Segmented, Skeleton, StateView, useToast, toggleAll, toggleOne, type ScreenState } from '@hana/ui';
import { autoAssign, byTab, dayOffset, filterQuotes, formatPrice, inquiryLate, inquiryOpen, nextSeat, searchQuotes, sortQuotes, statusCounts, statusOf } from './logic';
import type { Inquiry, Quote, QuoteStatus } from './types';
import { QuoteDock, STATUS_DOT } from './QuoteDock';
import { QuoteCompact } from './QuoteCompact';
import './quoteList.i18n';
import s from './quotes.module.css';

// Mint-Quote (desktop 1280 / 1536 / 1920): tab trạng thái · tìm + Lọc + Dán danh sách + Lưới giá · hàng chờ hỏi giá · bảng 9 cột.
// Chỉ đọc; nút ghi báo toast. Người xem chưa có quyền `money` (mặc định): không cột Margin / GP%, giá dưới sàn chỉ báo "Chờ duyệt".

type Tab = 'all' | QuoteStatus | 'Live';
const MAIN_TABS: readonly { key: Tab; label: string }[] = [
  { key: 'all', label: 'Tất cả' },
  { key: 'Đã gửi', label: 'Đã gửi' },
  { key: 'Khách chấp nhận', label: 'Khách chấp nhận' },
  { key: 'Hết hạn', label: 'Hết hạn' },
];
const MORE_TABS: readonly { key: Tab; label: string }[] = [
  { key: 'Nháp', label: 'Nháp' },
  { key: 'Đã thành Job', label: 'Đã thành Job' },
  { key: 'Live', label: 'Live' },
];
const STATUS_CLS: Record<QuoteStatus, string> = { 'Nháp': 'st0', 'Đã gửi': 'st1', 'Khách chấp nhận': 'st2', 'Hết hạn': 'st3', 'Đã thành Job': 'st4' };
const INQ_CLS: Record<Inquiry['status'], string> = { Asked: 'iAsk', Taken: 'iTake', Quoted: 'iQuo', Skip: 'iSkip' };

/** Rộng cột theo board (CW 1240): chọn 47 · số BG 140 · khách 186 · tuyến 180 · dịch vụ 110 · hãng 89 · hiệu lực 125 · giá 118 · sales 87 · trạng thái 159. */
const COLS = [
  { key: 'no', header: 'Số BG · Ngày', w: 140 },
  { key: 'cus', header: 'Khách · Người nhận', w: 186 },
  { key: 'rt', header: 'Tuyến · Transit', w: 180 },
  { key: 'sv', header: 'Dịch vụ', w: 110 },
  { key: 'cr', header: 'Hãng', w: 89 },
  { key: 'valid', header: 'Hiệu lực đến', w: 125, sort: true },
  { key: 'sell', header: 'Giá bán', w: 118, right: true },
  { key: 'sale', header: 'Sales', w: 87 },
  { key: 'st', header: 'Trạng thái', w: 159 },
] as const;
const TOTAL = 47 + COLS.reduce((a, c) => a + c.w, 0);
const pct = (w: number) => `${((w / TOTAL) * 100).toFixed(3)}%`;

const FILTERS = [
  { key: 'kinds', label: 'Dịch vụ', opts: ['Sea FCL', 'Reefer', 'Sea LCL', 'Air'] },
  { key: 'sales', label: 'Sales', opts: ['DAVID', 'PHILIP', 'TUANNT', 'ANDG'] },
] as const;

const Ab = ({ text, size = 20 }: { text: string; size?: number }) => (
  <span className={s.ab} style={{ width: size, height: size }} aria-hidden="true">
    {text.slice(0, 2).toUpperCase()}
  </span>
);

function useWide() {
  const q = '(min-width: 1800px)';
  const [w, setW] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setW(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return w;
}

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

/** iPad / điện thoại dùng thẻ (Mint-Quote-iPad, -Phone); desktop dùng bảng. */
export function QuoteList() {
  const layout = useLayout();
  return layout === 'pad' || layout === 'phone' ? <QuoteCompact phone={layout === 'phone'} /> : <QuoteTable />;
}

function QuoteTable() {
  const tx = useT();
  const toast = useToast();
  const { data, error, isLoading, isError, refetch } = useList('quote', { limit: 5000 });
  const forced = readState();
  const [tab, setTab] = useState<Tab>('all');
  const [moreOpen, setMoreOpen] = useState(false);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<{ kinds: string[]; sales: string[] }>({ kinds: [], sales: [] });
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);
  const [dense, setDense] = useState<'std' | 'dense'>('std');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [ov, setOv] = useState<Record<string, Partial<Inquiry>>>({});
  const inqData = useList('quoteInquiry', { limit: 100 }).data?.items ?? [];
  const seatData = useList('quoteSeat', { limit: 100 }).data?.items ?? [];
  const inq = useMemo(() => inqData.map((i) => ({ ...i, ...ov[i.id] })), [inqData, ov]);
  const [autoOn, setAutoOn] = useState(true);
  const [poolOpen, setPoolOpen] = useState(false);
  const wide = useWide();
  const [dockOpen, setDockOpen] = useState(true);

  const all = data?.items ?? [];
  const filtered = useMemo(() => filterQuotes(searchQuotes(all, q), filter), [all, q, filter]);
  const counts = statusCounts(filtered);
  const rows = useMemo(() => {
    const t = tab === 'all' ? null : tab;
    const base = byTab(filtered, t);
    return sortDir ? sortQuotes(base, 'valid', sortDir) : base;
  }, [filtered, tab, sortDir]);
  const keys = rows.map((r) => r.id);
  const hasFilter = filter.kinds.length + filter.sales.length > 0;

  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const soon = (what: string) => () => toast({ msg: `${what} chưa mở ở bản này` });

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';
  const ready = state === 'ready';
  const live = ready || state === 'empty'; // board: hàng chờ hỏi giá + dock giữ khi rỗng, chỉ ẩn khi loading / error

  const cnt = (k: Tab): number | undefined => (!ready ? undefined : k === 'all' ? filtered.length : k === 'Live' ? filtered.filter((r) => r.live).length : counts[k]);
  const inMore = MORE_TABS.some((t) => t.key === tab);
  const items = MAIN_TABS.map((t) => ({ key: t.key as string, label: t.label, n: cnt(t.key) }));
  const moreLabel = inMore ? MORE_TABS.find((t) => t.key === tab)!.label : 'Thêm';
  const pickTab = (k: string) => {
    setMoreOpen(false);
    setSelected(new Set());
    setTab(k as Tab);
  };

  // việc mở của ghế = việc nền + phiếu vừa nhận trong phiên này (phiếu đã Taken trong dữ liệu mẫu đã nằm sẵn trong việc nền)
  const seats = seatData.map((p) => ({ ...p, open: p.open + Object.values(ov).filter((v) => v.status === 'Taken' && v.to === p.user).length }));
  const next = nextSeat(seats);
  const hasAsked = inq.some((i) => i.status === 'Asked');
  const setOne = (id: string, patch: Partial<Inquiry>, msg: string) => {
    setOv((o) => ({ ...o, [id]: { ...o[id], ...patch } }));
    toast({ msg });
  };
  const autoAll = () => {
    const r = autoAssign(inq, seats);
    if (!r.done.length) return toast({ msg: 'Không còn phiếu Asked chờ chia' });
    setOv((o) => Object.fromEntries([...Object.entries(o), ...r.rows.filter((i, k) => i !== inq[k]).map((i) => [i.id, { ...o[i.id], status: i.status, to: i.to }] as const)]));
    setPoolOpen(false);
    toast({ msg: `Đã tự chia ${r.done.length} phiếu · ${r.done.join(', ')}` });
  };

  const toggleFilter = (f: 'kinds' | 'sales', v: string) =>
    setFilter((cur) => ({ ...cur, [f]: cur[f].includes(v) ? cur[f].filter((x) => x !== v) : [...cur[f], v] }));

  const inqSection = (inDock: boolean): ReactNode => (
          <section className={s.inq} aria-label="Hàng chờ hỏi giá">
            <div className={s.inqHead}>
              <div className={s.inqTitle}>
                {inDock ? <b className={s.inqB}>{tx('ql.queue')}</b> : <h2 title={tx('ql.queueTip')}>{tx('ql.queue')}</h2>}
                <span className={s.late}>{inquiryLate(inq)} sắp quá hạn</span>
              </div>
              {!inDock && (
              <div className={s.inqTools}>
                <button type="button" className={`${s.mini} ${autoOn ? s.miniOn : ''}`} aria-haspopup="dialog" aria-expanded={poolOpen} onClick={() => setPoolOpen((o) => !o)}>
                  <IconUserCog size={14} aria-hidden="true" />
                  {autoOn ? 'Tự chia: bật' : 'Tự chia: tắt'} · Phiếu mới kế tiếp → {next}
                </button>
                {hasAsked && (
                  <button type="button" className={s.mini} onClick={autoAll}>
                    <IconUserPlus size={14} aria-hidden="true" />
                    {tx('ql.autoAsk')}
                  </button>
                )}
                {poolOpen && (
                  <div className={s.pool} role="dialog" aria-label="Chia việc Pricing">
                    <div className={s.poolHead}>
                      <b>{tx('ql.split')}</b>
                      <button type="button" role="switch" aria-checked={autoOn} className={s.sw} onClick={() => setAutoOn((o) => !o)}>
                        {autoOn ? 'Tự chia: bật' : 'Tự chia: tắt'}
                      </button>
                    </div>
                    {seats.map((p) => (
                      <div key={p.user} className={s.seat}>
                        <b>{p.user}</b>
                        <span className={s.lb}>[PRC] {p.seat}</span>
                        {p.leave && <span className={s.warnTx}>{p.leave}</span>}
                        {!p.leave && p.user === next && <span className={s.nextTag}>{tx('ql.next')}</span>}
                        <span className={s.seatN}>{p.open} việc mở</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}
            </div>
            <div className={s.inqGrid}>
              {inq.map((r) => (
                <InquiryCard key={r.id} r={r} onSet={setOne} onToast={(msg) => toast({ msg })} />
              ))}
            </div>
          </section>
  );

  return (
    <Page kind="table" dock={wide && dockOpen && live ? <QuoteDock rows={filtered} inquiries={inqSection(true)} onClose={() => setDockOpen(false)} /> : undefined}>
      <PageHeader
        title="Báo giá"
        tabs={
          <div className={s.tabs}>
            <Segmented label="Trạng thái báo giá" value={inMore ? '' : (tab as string)} onChange={pickTab} items={items} />
            <button type="button" className={`${s.more} ${inMore ? s.moreOn : ''}`} aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)}>
              {moreLabel} ▾
            </button>
            {moreOpen && (
              <div className={s.pop} role="menu" aria-label="Trạng thái khác">
                {MORE_TABS.map((t) => (
                  <button key={t.key} type="button" role="menuitem" className={s.popItem} onClick={() => pickTab(t.key)}>
                    {t.label}
                    <span className={s.popN}>{cnt(t.key)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        }
        actions={
          <>
            <HeaderTool label="Phân tích" icon={IconChartBar} active={wide && dockOpen} onClick={wide ? () => setDockOpen((o) => !o) : soon('Phân tích')} />
            <HeaderTool label="Việc" icon={IconChecklist} n={5} />
            <HeaderTool label="Thông báo" icon={IconBell} n={2} />
            <HeaderTool label="Thao tác khác" icon={IconDots} onClick={soon('Menu thao tác')} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
              {tx('ql.create')}
            </Button>
          </>
        }
      />
      <div className={s.page}>
        <div className={s.tb}>
          <label className={s.search}>
            <IconSearch size={16} aria-hidden="true" />
            <input aria-label="Tìm báo giá" placeholder="Số BG / khách / tuyến / hãng" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className={s.filterWrap}>
            <button type="button" className={s.filter} aria-expanded={filterOpen} aria-haspopup="dialog" onClick={() => setFilterOpen((o) => !o)}>
              <IconPlus size={16} aria-hidden="true" />
              {tx('ql.filter')}
            </button>
            {filterOpen && (
              <div className={s.fpop} role="dialog" aria-label="Bộ lọc báo giá">
                {FILTERS.map((f) => (
                  <div key={f.key} className={s.fgrp}>
                    <span className={s.fk}>{f.label}</span>
                    {f.opts.map((o) => (
                      <button key={o} type="button" className={`${s.fopt} ${filter[f.key].includes(o) ? s.fon : ''}`} aria-pressed={filter[f.key].includes(o)} onClick={() => toggleFilter(f.key, o)}>
                        {o}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          {hasFilter && (
            <button type="button" className={s.clear} onClick={() => setFilter({ kinds: [], sales: [] })}>
              <IconX size={14} aria-hidden="true" />
              {tx('ql.clear')}
            </button>
          )}
          <button type="button" className={s.paste} onClick={soon('Dán danh sách hỏi giá')}>
            <IconFileText size={16} aria-hidden="true" />
            {tx('ql.paste')}
          </button>
          <button type="button" className={s.grid} onClick={soon('Lưới giá khách')}>
            <IconLayoutGrid size={16} aria-hidden="true" />
            {tx('ql.grid')}
          </button>
          {(
            <div className={s.sum}>
              <span>{rows.length} báo giá</span>
              <Segmented
                kind="toggle"
                label="Mật độ"
                value={dense}
                onChange={setDense}
                items={[
                  { key: 'std', label: 'Chuẩn' },
                  { key: 'dense', label: 'Gọn' },
                ]}
              />
            </div>
          )}
        </div>

        {live && !(wide && dockOpen) && inqSection(false)}

        <section aria-label={state === 'error' ? 'Lỗi' : state === 'empty' || (ready && rows.length === 0) ? 'Trống' : undefined}>
        <StateView
          state={live && rows.length === 0 ? 'empty' : state}
          title={live ? 'Chưa có báo giá nào' : state === 'error' ? 'Không tải được danh sách báo giá' : undefined}
          desc={live ? 'Không có báo giá trong kỳ 10/2026 khớp bộ lọc hiện tại.' : state === 'error' ? 'Mất kết nối tới máy chủ. Dữ liệu đang hiện có thể là bản cũ.' : undefined}
          icon={live ? <IconFileText size={26} stroke={1.8} /> : undefined}
          action={
            live ? (
              <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
                {tx('ql.create')}
              </Button>
            ) : state === 'error' ? (
              <Button onClick={() => void refetch()}>{tx('ql.retry')}</Button>
            ) : undefined
          }
          skeleton={<Skeleton rows={9} />}
        >
          <section className={s.card} aria-label="Danh sách báo giá">
            <table className={`${s.table} ${dense === 'dense' ? s.dense : ''} ${selected.size ? s.picking : ''}`} aria-label="Báo giá" aria-rowcount={rows.length + 1}>
              <colgroup>
                <col style={{ width: pct(47) }} />
                {COLS.map((c) => (
                  <col key={c.key} className={c.key === 'sale' ? s.colSale : undefined} style={{ width: pct(c.w) }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className={s.selCell}>
                    <input type="checkbox" className={s.check} aria-label="Chọn tất cả" checked={keys.length > 0 && selected.size === keys.length} onChange={() => setSelected(toggleAll(selected, keys))} />
                  </th>
                  {COLS.map((c) => (
                    <th key={c.key} scope="col" className={[c.key === 'sale' && s.colSale, 'right' in c && s.right].filter(Boolean).join(' ') || undefined} aria-sort={'sort' in c ? (sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none') : undefined}>
                      {'sort' in c ? (
                        <button type="button" className={s.sortBtn} onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
                          {c.header} {sortDir === 'asc' ? '↑' : sortDir === 'desc' ? '↓' : '↕'}
                        </button>
                      ) : (
                        c.header
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <QuoteRow key={r.id} r={r} index={i} on={selected.has(r.id)} onPick={() => setSelected(toggleOne(selected, r.id))} onOpen={soon('Chi tiết báo giá')} />
                ))}
              </tbody>
            </table>
            <div className={s.foot}>
              <span>Giá bán chính / cont · {rows.length} báo giá</span>
              <span>{tx('ql.hold')}</span>
            </div>
          </section>
        </StateView>
        </section>
      </div>
    </Page>
  );
}

function QuoteRow({ r, index, on, onPick, onOpen }: { r: Quote; index: number; on: boolean; onPick: () => void; onOpen: () => void }) {
  const st = statusOf(r);
  const validTone = r.validDays < 0 ? s.err : r.validDays <= 7 ? s.warnTx : undefined;
  return (
    <tr className={on ? s.rowOn : undefined} aria-rowindex={index + 2} aria-selected={on} onClick={onOpen}>
      <td className={s.selCell} onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" className={s.check} aria-label={`Chọn ${r.id}`} checked={on} onChange={onPick} />
      </td>
      <td>
        <span className={s.no}>{r.id}</span>
        <div className={s.sub}>{dayOffset(r.madeDays)}</div>
      </td>
      <td>
        <b className={s.cus}>{r.customer}</b>
        <div className={s.sub}>{r.contact}</div>
      </td>
      <td>
        {r.route}
        <div className={s.sub}>{r.transit}</div>
      </td>
      <td>
        {r.kind}
        <div className={s.sub}>{r.qty}</div>
      </td>
      <td>{r.carrier}</td>
      <td className={validTone}>{dayOffset(r.validDays)}</td>
      <td className={s.right} title={r.under ? 'Giá dưới sàn — cần [SAL-MGR] duyệt' : undefined}>
        {formatPrice(r.price)}
        <div className={`${s.sub} ${s.subR} ${r.under ? s.warnB : ''}`}>
          {r.under && <IconAlertTriangle size={12} aria-hidden="true" />}
          {r.under ? 'Chờ duyệt' : r.unit}
        </div>
      </td>
      <td className={s.colSale}>
        <span className={s.person} title={`Sales ${r.sale}`}>
          <Ab text={r.sale} />
          <span>{r.sale}</span>
        </span>
      </td>
      <td>
        <span className={`${s.st} ${s[STATUS_CLS[st]]}`}>
          <span className={s.stDot} style={{ background: STATUS_DOT[st] }} aria-hidden="true" />
          {st}
        </span>
      </td>
    </tr>
  );
}

function InquiryCard({ r, onSet, onToast }: { r: Inquiry; onSet: (id: string, patch: Partial<Inquiry>, msg: string) => void; onToast: (msg: string) => void }) {
  const tx = useT();
  const open = inquiryOpen(r);
  const Due = open ? IconClockHour4 : r.status === 'Quoted' ? IconCheck : IconForbid2;
  const dueTx = open ? `${r.hoursLeft}h LV` : r.status === 'Quoted' ? 'Đã trả' : 'Không chào';
  const dueCls = open ? (r.hoursLeft <= 4 ? s.err : r.hoursLeft <= 8 ? s.warnTx : s.lb) : s.lb;
  return (
    <div className={s.icard}>
      <div className={s.iTop}>
        <b className={s.iNo}>{r.id}</b>
        <span className={`${s.iSt} ${s[INQ_CLS[r.status]]}`}>{r.status}</span>
      </div>
      <div className={s.iRoute}>
        {r.route} · {r.carrier}
      </div>
      <div className={s.iMeta}>
        <span className={s.iChip} title={`Khách ${r.customer}`}>
          <IconBuilding size={13} aria-hidden="true" />
          {r.customer}
        </span>
        <span className={s.iChip} title={`Số lượng ${r.qty}`}>
          <IconPackage size={13} aria-hidden="true" />
          {r.qty}
        </span>
        <span className={`${s.iChip} ${dueCls}`} title={open ? `Còn ${r.hoursLeft} giờ làm việc` : dueTx}>
          <Due size={13} aria-hidden="true" />
          {dueTx}
        </span>
      </div>
      <div className={s.iWho} title={`Sale ${r.from} → Pricing ${r.to || 'chưa giao'}`}>
        <span className={s.person} title={`Sale yêu cầu ${r.from}`}>
          <Ab text={r.from} size={18} />
          <span>{r.from}</span>
        </span>
        →
        {r.to ? (
          <span className={s.person} title={`Pricing ${r.to}`}>
            <Ab text={r.to} size={18} />
            <span>{r.to}</span>
          </span>
        ) : (
          <span title="Chưa giao Pricing">—</span>
        )}
      </div>
      <div className={s.iActs}>
        {r.status === 'Asked' && (
          <button type="button" className={s.act} onClick={() => onSet(r.id, { status: 'Taken', to: 'DAVID' }, `Đã nhận ${r.id} · task PRC chuyển cho bạn`)}>
            <IconCheck size={14} aria-hidden="true" />
            {tx('ql.take')}
          </button>
        )}
        {r.status === 'Taken' && (
          <>
            <button type="button" className={s.act} onClick={() => onToast(`Mở Giá & lịch tàu › ${r.lane}${r.carrierName} để nhập giá`)}>
              <IconRoute size={14} aria-hidden="true" />
              {tx('ql.openRoute')}
            </button>
            <button type="button" className={s.act} onClick={() => onSet(r.id, { status: 'Quoted', rateCode: `${r.lane}${r.carrierName}-SN261105` }, `Đã ghi nhận có giá ${r.id}`)}>
              <IconCheck size={14} aria-hidden="true" />
              {tx('ql.hasPrice')}
            </button>
            <button type="button" className={s.act} onClick={() => onSet(r.id, { status: 'Skip', why: 'Hãng không chào' }, `Đã bỏ qua ${r.id}`)}>
              <IconPlayerSkipForward size={14} aria-hidden="true" />
              {tx('ql.skip')}
            </button>
          </>
        )}
        {r.status === 'Quoted' && (
          <>
            {!r.ack && (
              <>
                <button type="button" className={s.act} onClick={() => onSet(r.id, { ack: true }, `Đã xác nhận nhận giá ${r.rateCode} · đóng việc chờ của Sale`)}>
                  <IconCheck size={14} aria-hidden="true" />
                  {tx('ql.gotPrice')}
                </button>
                <button type="button" className={s.act} onClick={() => onToast(`Mở soạn báo giá ${r.customer} từ ${r.rateCode}`)}>
                  <IconFileText size={14} aria-hidden="true" />
                  {tx('ql.quote')}
                </button>
              </>
            )}
            {r.ack && <span className={s.lb}>Sale đã nhận · {r.rateCode}</span>}
          </>
        )}
        {r.status === 'Skip' && (
          <>
            <button type="button" className={s.act} onClick={() => onSet(r.id, { status: 'Asked', to: '' }, `Đã hỏi lại ${r.id} · vào lại hàng chờ Pricing`)}>
              <IconMessageCircle size={14} aria-hidden="true" />
              {tx('ql.reask')}
            </button>
            <span className={s.lb}>{r.why}</span>
          </>
        )}
      </div>
    </div>
  );
}
